import { isChannelMuted } from '@liveforge/shared';
import { Button } from '../ui/button';
import { useStudioStore } from '../../stores/studio-store';

export interface OnAirToggleProps {
  /**
   * Built with `audioChannelId` from a *real* publication's track name, never
   * from an assumed default: the mixer derives its own key the same way, and
   * the two only meet if both read the same publication.
   */
  channelId: string;
  disabled?: boolean;
}

/**
 * Takes one audio source on or off air.
 *
 * This is not the same power as the Mute button beside it, and the two are
 * worded differently on purpose. Mute reaches into the guest's browser and
 * switches their microphone off: they see it happen, and LiveKit refuses to
 * switch it back on remotely. **On air touches nobody's device** - it only
 * decides whether that audio reaches the broadcast, and it can always be taken
 * back.
 *
 * A director wants the second one. The first is for silencing someone at the
 * source.
 */
export function OnAirToggle({ channelId, disabled = false }: OnAirToggleProps) {
  const muted = useStudioStore((state) => isChannelMuted(state.audio, channelId));
  const setChannelMuted = useStudioStore((state) => state.setChannelMuted);

  return (
    <Button
      variant="ghost"
      small
      disabled={disabled}
      className={muted ? 'on-air on-air--off' : 'on-air on-air--live'}
      title={
        muted
          ? 'Off air: this source is not in the broadcast. Their own microphone is untouched.'
          : 'On air: this source is in the broadcast.'
      }
      onClick={() => setChannelMuted(channelId, !muted)}
    >
      {muted ? 'Off air' : 'On air'}
    </Button>
  );
}
